import PaymentMethodsList from '@/components/templates/checkout/PaymentMethodList/PaymentMethodsList';
import {
  APPLE_PAY_LIST_ITEM,
  BOKADIREKT_GIFTCARD_LIST_ITEM,
  BOKADIREKT_VALUECARD_LIST_ITEM,
  BOKADIREKT_WELLNESSCARD_LIST_ITEM,
  CHECKOUT_PAYMENT_METHOD,
  COUPON_PAYMENT_METHOD,
  GOOGLE_PAY_LIST_ITEM,
  KLARNA_LIST_ITEM,
  NEW_ONLINE_CARD_LIST_ITEM,
  PAY_AT_PLACE_LIST_ITEM,
  QLIRO_LIST_ITEM,
  STORED_ONLINE_CARD_LIST_ITEM,
  SWISH_LIST_ITEM,
  WALLEY_LIST_ITEM,
} from '@/constants/checkout';
import { usePaymentMethodsContextSafe } from '@/features/payments/hooks/usePaymentMethods';
import { isHidden, isHighlighted } from '@/helpers/checkout';
import { useCardsContext } from '@/hooks/adyen/useCards';
import { TriggerSource } from '@/types/analytics';
import {
  AvailablePaymentMethod,
  CheckoutSummary,
  CouponMethod,
  PaymentMethodListItem,
  SelectedPaymentMethod,
} from '@/types/checkout';

function mapPaymentMethodHiddenProp(paymentMethod: PaymentMethodListItem, forceHide?: boolean): PaymentMethodListItem {
  return { ...paymentMethod, hidden: forceHide || isHidden(paymentMethod) };
}

function mapPaymentMethodSelectedProp(
  paymentMethod: PaymentMethodListItem,
  selectedPaymentMethod?: SelectedPaymentMethod,
  alreadySelected?: { value: boolean },
): PaymentMethodListItem {
  let isSelected = false;
  if (selectedPaymentMethod?.type === paymentMethod.type) {
    if (paymentMethod.type === CHECKOUT_PAYMENT_METHOD.STORED_COF) {
      if ('id' in selectedPaymentMethod) {
        if (selectedPaymentMethod.id === paymentMethod.id) {
          isSelected = true;
        }
      } else {
        if (!alreadySelected?.value) {
          isSelected = true;
        }
      }
    } else {
      isSelected = true;
    }
  }

  if (isSelected) {
    alreadySelected.value = true;
  }

  return { ...paymentMethod, isSelected };
}

function mapPaymentMethodHighlightedProp(paymentMethod: PaymentMethodListItem): PaymentMethodListItem {
  return {
    ...paymentMethod,
    highlighted: isHighlighted(paymentMethod.type),
  };
}

const paymentMethodsToHideMap: {
  [key in TriggerSource]?: PaymentMethodListItem['type'][];
} = {
  checkout_booking_update_payment_method: [
    COUPON_PAYMENT_METHOD.GIFTCARD,
    COUPON_PAYMENT_METHOD.VALUECARD,
    COUPON_PAYMENT_METHOD.WELLNESSCARD,
    CHECKOUT_PAYMENT_METHOD.PAY_AT_PLACE,
  ],
  checkout_bundle_summary: [
    COUPON_PAYMENT_METHOD.GIFTCARD,
    COUPON_PAYMENT_METHOD.VALUECARD,
    COUPON_PAYMENT_METHOD.WELLNESSCARD,
    CHECKOUT_PAYMENT_METHOD.KLARNA,
    CHECKOUT_PAYMENT_METHOD.PAY_AT_PLACE,
  ],
};

function sortPaymentMethodByHiddenProp(a: PaymentMethodListItem, b: PaymentMethodListItem) {
  return Number(a.hidden) - Number(b.hidden);
}

const PickPaymentMethod = ({
  summary,
  onPaymentSelected,
  triggerSource,
  selectedPaymentMethod,
  methodsVisible = 0,
  useHighlighted = false,
  isOnlinePaymentRequiredCustomer = false,
  isGiftcardCheckout = false,
}: {
  summary: CheckoutSummary;
  triggerSource: TriggerSource;
  selectedPaymentMethod?: SelectedPaymentMethod;
  methodsVisible?: number;
  useHighlighted?: boolean;
  isOnlinePaymentRequiredCustomer?: boolean;
  onPaymentSelected: (method: SelectedPaymentMethod | CouponMethod) => void;
  isGiftcardCheckout?: boolean;
}) => {
  const { cards } = useCardsContext();
  const paymentMethodsContext = usePaymentMethodsContextSafe();
  const isBokadirektPay = paymentMethodsContext?.isBokadirektPay ?? false;
  const storedCardOnFileMethods = isBokadirektPay ? paymentMethodsContext?.storedPaymentMethods : cards;
  const { availablePaymentMethods, acceptsGiftcard, acceptsValuecard, acceptsWellnesscard } = summary;

  /* Add only first saved card if multiple, the others will be added at 
  the bottom of the list, before the giftcards */
  const storedCoFPaymentMethod =
    storedCardOnFileMethods?.map((card) => STORED_ONLINE_CARD_LIST_ITEM({ ...card })) || [];
  const firstStoredCoF = storedCoFPaymentMethod.shift();
  let showStoredCoF = false;

  const mapPaymentMethodListItem = (
    paymentMethod: AvailablePaymentMethod,
  ): PaymentMethodListItem | PaymentMethodListItem[] => {
    switch (paymentMethod.type) {
      case CHECKOUT_PAYMENT_METHOD.PAY_AT_PLACE:
        return { ...PAY_AT_PLACE_LIST_ITEM, ...paymentMethod };
      case CHECKOUT_PAYMENT_METHOD.KLARNA: {
        if (isBokadirektPay) {
          const klarnaBrands = paymentMethodsContext?.paymentMethods.find((pm) => pm.type === 'Klarna')?.brands ?? [];
          if (klarnaBrands.length === 0) return [];
        }
        return { ...KLARNA_LIST_ITEM, ...paymentMethod };
      }
      case CHECKOUT_PAYMENT_METHOD.QLIRO:
        return { ...QLIRO_LIST_ITEM, ...paymentMethod };
      case CHECKOUT_PAYMENT_METHOD.NEW_COF:
        return { ...NEW_ONLINE_CARD_LIST_ITEM, ...paymentMethod };
      case CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY:
        return { ...GOOGLE_PAY_LIST_ITEM, ...paymentMethod };
      case CHECKOUT_PAYMENT_METHOD.APPLE_PAY:
        return { ...APPLE_PAY_LIST_ITEM, ...paymentMethod };
      case CHECKOUT_PAYMENT_METHOD.STORED_COF: {
        showStoredCoF = true;
        return firstStoredCoF ? { ...firstStoredCoF, hidden: paymentMethod.hidden } : [];
      }
      case CHECKOUT_PAYMENT_METHOD.SWISH:
        return { ...SWISH_LIST_ITEM, ...paymentMethod };
      case CHECKOUT_PAYMENT_METHOD.WALLEY:
        return { ...WALLEY_LIST_ITEM, ...paymentMethod };
      default:
        return null;
    }
  };

  const showGiftcard = acceptsGiftcard;
  const showValuecard = acceptsValuecard;
  const showWellnesscard = acceptsWellnesscard;

  let alreadySelected = { value: false };
  const hasOnlinePaymentMethod =
    [
      ...availablePaymentMethods.filter(
        (method) =>
          method.type !== CHECKOUT_PAYMENT_METHOD.NONE && method.type !== CHECKOUT_PAYMENT_METHOD.PAY_AT_PLACE,
      ),
    ].length !== 0;
  const hideCoupons = hasOnlinePaymentMethod;

  const supportedMethods: PaymentMethodListItem[] = [
    ...availablePaymentMethods
      .filter((method) => method.type !== CHECKOUT_PAYMENT_METHOD.NONE)
      .map((method) => mapPaymentMethodListItem(method))
      .flat()
      .sort(sortPaymentMethodByHiddenProp), // show first not hidden methods
    ...(showStoredCoF && Boolean(storedCoFPaymentMethod.length)
      ? storedCoFPaymentMethod.map((method) => ({ ...method, hidden: true }))
      : []),
    ...(showGiftcard
      ? [hideCoupons ? { ...BOKADIREKT_GIFTCARD_LIST_ITEM, hidden: hideCoupons } : BOKADIREKT_GIFTCARD_LIST_ITEM]
      : []),
    ...(showValuecard
      ? [hideCoupons ? { ...BOKADIREKT_VALUECARD_LIST_ITEM, hidden: hideCoupons } : BOKADIREKT_VALUECARD_LIST_ITEM]
      : []),
    ...(showWellnesscard
      ? [
          hideCoupons
            ? { ...BOKADIREKT_WELLNESSCARD_LIST_ITEM, hidden: hideCoupons }
            : BOKADIREKT_WELLNESSCARD_LIST_ITEM,
        ]
      : []),
  ]
    .map((method, index) => mapPaymentMethodHiddenProp(method, methodsVisible && methodsVisible <= index))
    .map((method, index) => mapPaymentMethodSelectedProp(method, selectedPaymentMethod, alreadySelected))
    .map((method) => (useHighlighted ? mapPaymentMethodHighlightedProp(method) : method));

  const unsupportedMethods: PaymentMethodListItem[] = [];
  const hasUnsupportedMethods = !!unsupportedMethods.length;

  return (
    <>
      <PaymentMethodsList
        paymentMethods={supportedMethods}
        onClick={onPaymentSelected}
        isGiftcardCheckout={isGiftcardCheckout}
        // With no online method to pay with, telling the customer to pay online would be a dead end.
        isOnlinePaymentRequiredCustomer={isOnlinePaymentRequiredCustomer && hasOnlinePaymentMethod}
        isBokadirektPay={isBokadirektPay}
        type="supported"
      />
      {hasUnsupportedMethods && (
        <PaymentMethodsList paymentMethods={unsupportedMethods} onClick={() => null} type="unsupported" />
      )}
    </>
  );
};

export default PickPaymentMethod;
